/** Print / Thermal / PDF / WhatsApp-share actions for the mobile slip. */
import { useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Button } from "@/components/ui/button";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { Printer, Download, FileImage, FileText, MessageSquare } from "lucide-react";
import { nativeShare } from "@/lib/native";
import { printElement, printThermal } from "@/components/print/printUtils";

export default function SlipToolbar({ targetRef, shareTitle, shareText, waHref, filename, txtText, smsText }) {
  const [downloading, setDownloading] = useState(false);
  const [jpgBusy, setJpgBusy] = useState(false);
  const baseName = filename.replace(/\.pdf$/i, "");

  const triggerDownload = (href, name) => {
    const a = document.createElement("a");
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // JPG export — SAME rendered slip (same data, same QR), just rasterized.
  const onDownloadJpg = async () => {
    if (!targetRef.current || jpgBusy) return;
    setJpgBusy(true);
    try {
      const canvas = await html2canvas(targetRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      triggerDownload(canvas.toDataURL("image/jpeg", 0.92), `${baseName}.jpg`);
    } catch (err) {
      console.error("JPG export failed", err);
    } finally {
      setJpgBusy(false);
    }
  };

  // Lightweight TXT receipt.
  const onDownloadTxt = () => {
    const blob = new Blob([txtText || shareText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `${baseName}.txt`);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  // Opens the device's native SMS composer — nothing is sent automatically.
  const onSms = () => {
    window.location.href = `sms:?&body=${encodeURIComponent(smsText || shareText)}`;
  };

  const onShare = async () => {
    // Native share sheet on Android; navigator.share on modern browsers; wa.me deep-link everywhere else.
    const ok = await nativeShare({ title: shareTitle, text: shareText, dialogTitle: shareTitle });
    if (!ok) window.open(waHref, "_blank", "noopener");
  };

  const onDownloadPdf = async () => {
    if (!targetRef.current || downloading) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(targetRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      // A5 portrait — 148 x 210 mm, card centered with 8 mm margins.
      const pdf = new jsPDF({ unit: "mm", format: "a5", orientation: "portrait" });
      const pageWidthMm = 148;
      const pageHeightMm = 210;
      const targetWidth = pageWidthMm - 16;
      const ratio = canvas.height / canvas.width;
      let imgWidth = targetWidth;
      let imgHeight = imgWidth * ratio;
      if (imgHeight > pageHeightMm - 16) {
        imgHeight = pageHeightMm - 16;
        imgWidth = imgHeight / ratio;
      }
      const x = (pageWidthMm - imgWidth) / 2;
      const y = (pageHeightMm - imgHeight) / 2;
      pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
      pdf.save(filename);
    } catch (err) {
      console.error("PDF export failed", err);
      // Fallback to the native print dialog so users are never blocked.
      printElement(targetRef.current);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2 no-print">
      <Button size="sm" className="am-btn-primary rounded-full" onClick={() => printElement(targetRef.current)} data-testid="slip-print-btn">
        <Printer className="w-4 h-4 mr-1.5" /> Print
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => printThermal(targetRef.current)} data-testid="slip-thermal-btn">
        <Printer className="w-4 h-4 mr-1.5" /> Thermal 58mm
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={onDownloadPdf} disabled={downloading} data-testid="slip-pdf-btn">
        <Download className="w-4 h-4 mr-1.5" /> {downloading ? "Rendering…" : "Download PDF"}
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={onDownloadJpg} disabled={jpgBusy} data-testid="slip-jpg-btn">
        <FileImage className="w-4 h-4 mr-1.5" /> {jpgBusy ? "Rendering…" : "Download JPG"}
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={onDownloadTxt} data-testid="slip-txt-btn">
        <FileText className="w-4 h-4 mr-1.5" /> Download TXT
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={onSms} data-testid="slip-sms-btn">
        <MessageSquare className="w-4 h-4 mr-1.5" /> Share via SMS
      </Button>
      <Button size="sm" variant="outline" className="rounded-full ring-1 ring-[#25D366]/30 hover:ring-[#25D366]/60 text-[#0f6b3a] hover:text-[#0f6b3a]" onClick={onShare} data-testid="slip-share-btn">
        <WhatsAppIcon className="w-5 h-5 mr-1.5" color="#25D366" /> Share on WhatsApp
      </Button>
    </div>
  );
}
